/**
 * useEmployeeScope — DRY template for employee-scoped views (training, compliance).
 *
 * Similar to useSubjectScope but filters by HUMAN actors (employees) instead of
 * SUBJECT actors (residents/patients).
 *
 * ## Usage
 *
 * ```tsx
 * const scope = useEmployeeScope({ nameResolver: resolveActorName });
 * // Then: <EmployeeFilterHeader scope={scope} />
 * //        <TabulatorPrimitiveManager overrideData={scope.employeeTasksOverride} />
 * ```
 *
 * @see useSubjectScope — companion hook for subject (resident/patient) filtering
 */

import { useState, useMemo } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

// ---------------------------------------------------------------------------
// GraphQL
// ---------------------------------------------------------------------------

/** List all actors — we filter to HUMAN on the client. */
const LIST_ACTORS_QUERY = gql`
  query ListActorsForEmployeeScope($limit: Int!) {
    listActors(limit: $limit) {
      items {
        id
        name
        actorType
        active
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ActorItem {
  id: string;
  name: string;
  actorType: string;
  active: boolean;
}

interface ListActorsData {
  listActors: { items: ActorItem[] };
}

/** Filter mode for the training/employee list. */
export type EmployeeScopeFilter = 'all' | 'my' | 'employee';

/** An employee option for the dropdown. */
export interface EmployeeOption {
  /** Actor sequential ID (e.g., "17_42") */
  value: string;
  /** Display name (resolved from ActorNameContext or raw ID as fallback) */
  label: string;
  /** Whether the employee is active */
  active: boolean;
}

export interface UseEmployeeScopeProps {
  /**
   * Function to resolve an actor ID to a display name.
   * Use `useResolveActorName()` from ActorNameContext.
   * If omitted, actor IDs are shown as-is.
   */
  nameResolver?: (actorId: string) => string;
  /**
   * Whether to show only active employees.
   * Defaults to true.
   */
  activeOnly?: boolean;
}

export interface UseEmployeeScopeReturn {
  /** Currently selected filter tab */
  filter: EmployeeScopeFilter;
  setFilter: (f: EmployeeScopeFilter) => void;

  /** Currently selected employee actor ID */
  selectedEmployeeId: string | null;
  setSelectedEmployeeId: (id: string | null) => void;

  /** Employee dropdown options */
  employeeOptions: EmployeeOption[];
  employeesLoading: boolean;

  /** Display label for the employee tab */
  employeeLabel: string;

  /** Display name of the selected employee */
  selectedEmployeeName: string;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useEmployeeScope(props: UseEmployeeScopeProps = {}): UseEmployeeScopeReturn {
  const { nameResolver, activeOnly = true } = props;

  const [filter, setFilter] = useState<EmployeeScopeFilter>('all');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);

  const handleSetFilter = (f: EmployeeScopeFilter) => {
    setFilter(f);
    if (f !== 'employee') setSelectedEmployeeId(null);
  };

  // Employee label — domain-agnostic
  const employeeLabel = 'Employee';

  // List HUMAN actors for employee dropdown
  const isEmployeeTab = filter === 'employee';
  const { data: actorData, loading: actorsLoading } = useQuery<ListActorsData>(LIST_ACTORS_QUERY, {
    variables: { limit: 200 },
    skip: !isEmployeeTab,
  });

  // Build employee options from HUMAN actors
  const employeeOptions: EmployeeOption[] = useMemo(() => {
    const resolve = nameResolver ?? ((id: string) => id);
    const items = actorData?.listActors?.items ?? [];

    return items
      .filter((a) => a.actorType === 'HUMAN')
      .filter((a) => (activeOnly ? a.active : true))
      .map((a) => ({
        value: a.id,
        label: a.name || resolve(a.id),
        active: a.active,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [actorData, nameResolver, activeOnly]);

  const employeesLoading = actorsLoading;

  // Selected employee display
  const resolve = nameResolver ?? ((id: string) => id);

  const selectedEmployeeName = useMemo(() => {
    if (!selectedEmployeeId) return '';
    return (
      employeeOptions.find((o) => o.value === selectedEmployeeId)?.label ||
      resolve(selectedEmployeeId)
    );
  }, [employeeOptions, selectedEmployeeId, resolve]);

  return {
    filter,
    setFilter: handleSetFilter,
    selectedEmployeeId,
    setSelectedEmployeeId,
    employeeOptions,
    employeesLoading,
    employeeLabel,
    selectedEmployeeName,
  };
}
